import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Jacket sets (blocks/set-builder.liquid), ported from the main shop's pdp-set-builder.js (winner of A/B test O1).
 * The cards and their prices are server-rendered; this element opens the panel for a card, builds one slot per
 * jacket (Fit + Size), and adds the slots as plain cart lines.
 *
 * Behaviour (as in the main shop):
 *   - a card toggles the panel in place; tapping the open card again closes it (back to a single purchase);
 *   - slot order = the card's image row: the page's jacket, then the other Fits of its family, cycling
 *     (3 jackets on the Men PDP = Men, Women, Men); a slot follows the buy box's size until the shopper edits it,
 *     a slot of the other Fit starts on that product's `default_size` ("M", "S" or "middle");
 *   - sold-out sizes are disabled; a slot without an available size keeps the button on "Choose {option} first";
 *   - the × of a slot removes that jacket, the tier follows the count (3 → 2 → closed);
 *   - while the panel is open the buy box's own add-to-cart is hidden (CSS on [data-open], assets/set-builder.css);
 *   - "Add set to cart" posts all slots to /cart/add.js (quantity 1, no properties), updates the cart through
 *     Horizon's CartLinesUpdateEvent (cart items, count bubble) and opens the cart drawer.
 *   - card prices follow the buy box: on a variant change they are taken from the page HTML Horizon re-rendered.
 *
 * @typedef {{ id: number, o: string[], available: boolean, img: string }} Variant
 * @typedef {{ handle: string, group: string, default_size: string | null, title: string, options: string[],
 *   img: string, variants: Variant[] }} PoolProduct
 * @typedef {{ handle: string, sel: (string | null)[], base: string | null, touched: boolean }} Slot
 * @typedef {{ config: HTMLScriptElement, panel: HTMLElement, slots: HTMLElement, actions: HTMLElement,
 *   error: HTMLElement, cards: HTMLButtonElement[] }} Refs
 * @extends {Component<Refs>}
 */
class SetBuilderComponent extends Component {
  requiredRefs = ['config', 'panel', 'slots', 'actions', 'error'];

  /** @type {any} */
  #cfg = null;
  /** @type {string} */
  #cfgText = '';
  /** @type {PoolProduct[]} */
  #pool = [];
  /** @type {number | null} */
  #tier = null;
  /** @type {Slot[]} */
  #slots = [];
  /** @type {string} */
  #variantId = '';
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section = null;
    this.#setOpen(false);
  }

  /** A re-render of the whole product (Horizon morphs `main` for another product) brings a new config. */
  updatedCallback() {
    super.updatedCallback();
    if (this.refs.config?.textContent !== this.#cfgText) this.#init();
  }

  #init() {
    this.#cfgText = this.refs.config?.textContent ?? '';
    try {
      this.#cfg = JSON.parse(this.#cfgText);
    } catch {
      this.#cfg = null;
    }
    const cfg = this.#cfg;
    if (!cfg || !Array.isArray(cfg.pool) || !cfg.pool.length || !Array.isArray(cfg.tiers) || !cfg.tiers.length) {
      this.hidden = true;
      return;
    }
    this.#pool = cfg.pool.filter((p) => Array.isArray(p.variants) && p.variants.length && Array.isArray(p.options));
    this.#variantId = String(cfg.variant ?? '');
    this.#tier = null;
    this.#slots = [];
    this.#markCards();
    this.#close();
  }

  /* ── catalogue helpers ──────────────────────────────────────────────────────────────────── */

  get #current() {
    return this.#pool[0];
  }

  /** @param {string} handle */
  #product(handle) {
    return this.#pool.find((p) => p.handle === handle) || this.#current;
  }

  /** the size option of a product: the option named "Size", else its last option @param {PoolProduct} p */
  #sizeIndex(p) {
    const i = p.options.findIndex((name) => /^size$/i.test(String(name).trim()));
    return i >= 0 ? i : p.options.length - 1;
  }

  /** @param {Variant} v @param {(string | null)[]} sel */
  #matches(v, sel) {
    return sel.every((val, i) => val == null || v.o[i] === val);
  }

  /** the ONE variant a selection names, or null while an option with several values is still open
   *  @param {PoolProduct} p @param {(string | null)[]} sel */
  #resolve(p, sel) {
    const full = sel.slice();
    for (let i = 0; i < p.options.length; i++) {
      if (full[i] != null) continue;
      const values = new Set(p.variants.map((v) => v.o[i]));
      if (values.size !== 1) return null;
      full[i] = [...values][0] ?? null;
    }
    return p.variants.find((v) => this.#matches(v, full)) || null;
  }

  /** fill the open values with available ones; the size takes `default_size` when it is available.
   *  An option without any available value stays open. @param {PoolProduct} p @param {(string | null)[]} sel */
  #complete(p, sel) {
    const out = sel.slice();
    const sizeIndex = this.#sizeIndex(p);
    for (let i = 0; i < p.options.length; i++) {
      if (out[i] != null) continue;
      const available = [];
      for (const v of p.variants) {
        if (!v.available || !this.#matches(v, out.slice(0, i))) continue;
        if (!available.includes(v.o[i])) available.push(v.o[i]);
      }
      let pick = available[0] ?? null;
      if (i === sizeIndex && p.default_size && available.length) {
        if (p.default_size === 'middle') pick = available[Math.floor((available.length - 1) / 2)] ?? pick;
        else if (available.includes(p.default_size)) pick = p.default_size;
      }
      out[i] = pick;
    }
    return out;
  }

  /** a selection whose variant is sold out loses its size and is completed again @param {PoolProduct} p
   *  @param {(string | null)[]} sel */
  #sanitize(p, sel) {
    const v = this.#resolve(p, sel);
    if (!v || v.available) return this.#complete(p, sel);
    const out = sel.slice();
    out[this.#sizeIndex(p)] = null;
    return this.#complete(p, out);
  }

  /** pickers for the product's options with more than one value @param {PoolProduct} p
   *  @param {(string | null)[]} sel */
  #pickers(p, sel) {
    return p.options
      .map((name, index) => {
        const values = [];
        for (const v of p.variants) {
          if (!this.#matches(v, sel.slice(0, index))) continue;
          if (!values.includes(v.o[index])) values.push(v.o[index]);
        }
        return {
          name: index === this.#sizeIndex(p) ? this.#cfg.copy.size || name : name,
          index,
          value: sel[index] ?? null,
          values: values.map((value) => {
            const probe = sel.slice(0, index);
            probe[index] = value;
            return { value, available: p.variants.some((v) => v.available && this.#matches(v, probe)) };
          }),
        };
      })
      .filter((picker) => picker.values.length > 1);
  }

  /** the groups (Fits) of the pool in pool order, the page's own first */
  get #groups() {
    const out = [];
    for (const p of this.#pool) if (!out.includes(p.group)) out.push(p.group);
    return out;
  }

  /** @param {string} group */
  #inGroup(group) {
    return this.#pool.find((p) => p.group === group) || null;
  }

  /** what the buy box currently sells: the page product and the options of its selected variant */
  #mainSelection() {
    const p = this.#current;
    const v = p.variants.find((x) => String(x.id) === this.#variantId) || null;
    return { product: p, sel: v ? v.o.slice() : p.options.map(() => null), key: this.#variantId };
  }

  /** a fresh slot i: the set's group order (own Fit first, then the others, cycling) @param {number} i */
  #freshSlot(i) {
    const main = this.#mainSelection();
    const groups = this.#groups;
    const target = this.#inGroup(groups[i % groups.length] ?? main.product.group) || main.product;
    if (target === main.product) {
      return { handle: target.handle, sel: this.#sanitize(target, main.sel), base: main.key, touched: false };
    }
    // another Fit: the size does not carry over (a partner's size is not the shopper's own) → its default size
    return { handle: target.handle, sel: this.#complete(target, target.options.map(() => null)), base: main.key, touched: false };
  }

  /* ── tracking ───────────────────────────────────────────────────────────────────────────── */

  /** @param {string} action @param {Record<string, unknown>} [extra] */
  #track(action, extra) {
    const dataLayer = /** @type {any} */ (window).dataLayer;
    if (!Array.isArray(dataLayer)) return;
    /** @type {Record<string, unknown>} */
    const event = { event: 'set_builder', set_action: action };
    if (extra) for (const [key, value] of Object.entries(extra)) event[`set_${key}`] = value;
    dataLayer.push(event);
  }

  /* ── cards and panel ────────────────────────────────────────────────────────────────────── */

  /** card click (on:click in blocks/set-builder.liquid) @param {Event} event */
  selectTier(event) {
    if (!this.#cfg) return;
    const card = /** @type {HTMLElement} */ (event.target);
    let index = /** @type {number | null} */ (Number(card.dataset.tier));
    if (!Number.isInteger(index) || !this.#cfg.tiers[index]) return;
    if (index === this.#tier) index = null;
    this.#tier = index;
    this.#markCards();
    if (index == null) {
      this.#close();
      this.#track('close');
      return;
    }
    const qty = Number(this.#cfg.tiers[index].min_qty);
    while (this.#slots.length < qty) this.#slots.push(this.#freshSlot(this.#slots.length));
    this.#slots.length = qty;
    this.refs.panel.hidden = false;
    this.#setOpen(true);
    this.#clearError();
    this.#renderPanel();
    this.#track('select', { tier: index, qty });
  }

  #markCards() {
    for (const card of this.refs.cards ?? []) {
      card.setAttribute('aria-checked', Number(card.dataset.tier) === this.#tier ? 'true' : 'false');
    }
  }

  /** close and EMPTY the panel: a hidden panel holds nothing focusable */
  #close() {
    this.refs.panel.hidden = true;
    this.refs.slots.replaceChildren();
    this.refs.actions.replaceChildren();
    this.#setOpen(false);
  }

  /** @param {boolean} open */
  #setOpen(open) {
    this.toggleAttribute('data-open', open);
  }

  /** @param {number} i */
  #removeSlot(i) {
    if (i < 0 || i >= this.#slots.length) return;
    this.#slots.splice(i, 1);
    let index = null;
    this.#cfg.tiers.forEach((/** @type {any} */ t, /** @type {number} */ k) => {
      if (this.#slots.length >= Number(t.min_qty)) index = k;
    });
    this.#tier = index;
    this.#markCards();
    if (index == null) {
      this.#slots.length = 0;
      this.#close();
      this.#track('close');
      return;
    }
    this.#renderPanel();
    this.#track('remove', { tier: index, qty: this.#slots.length });
  }

  #renderPanel() {
    this.refs.slots.replaceChildren(...this.#slots.map((slot, i) => this.#renderSlot(slot, i)));
    this.#renderActions();
  }

  /** @param {string} label @param {{ value: string, label: string, disabled?: boolean }[]} options
   *  @param {string} selected @param {(value: string) => void} onChange */
  #select(label, options, selected, onChange) {
    const field = document.createElement('div');
    field.className = 'set-builder__field';
    const id = `SetBuilder-${this.dataset.blockId}-${Math.random().toString(36).slice(2, 8)}`;
    const labelEl = document.createElement('label');
    labelEl.className = 'set-builder__label';
    labelEl.htmlFor = id;
    labelEl.textContent = label;
    const wrap = document.createElement('div');
    wrap.className = 'set-builder__select-wrap';
    const select = document.createElement('select');
    select.className = 'set-builder__select';
    select.id = id;
    for (const o of options) {
      const option = document.createElement('option');
      option.value = o.value;
      option.textContent = o.label;
      option.disabled = Boolean(o.disabled);
      option.selected = o.value === selected;
      select.append(option);
    }
    select.addEventListener('change', () => onChange(select.value));
    wrap.append(select);
    field.append(labelEl, wrap);
    return field;
  }

  /** @param {Slot} slot @param {number} i */
  #renderSlot(slot, i) {
    const copy = this.#cfg.copy;
    const p = this.#product(slot.handle);
    const v = this.#resolve(p, slot.sel) || p.variants.find((x) => this.#matches(x, slot.sel)) || null;

    const el = document.createElement('div');
    el.className = 'set-builder__slot';

    const head = document.createElement('div');
    head.className = 'set-builder__slot-head';
    const title = document.createElement('span');
    title.className = 'set-builder__slot-title';
    title.textContent = fill(copy.slot_title, { i: i + 1 });
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'set-builder__remove';
    remove.setAttribute('aria-label', fill(copy.remove, { i: i + 1 }));
    remove.textContent = '×';
    remove.addEventListener('click', () => this.#removeSlot(i));
    head.append(title, remove);

    const media = document.createElement('div');
    media.className = 'set-builder__slot-media';
    const src = v?.img || p.img;
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = p.title;
      img.width = 240;
      img.height = 240;
      img.loading = 'lazy';
      media.append(img);
    }

    const body = document.createElement('div');
    body.className = 'set-builder__slot-body';

    const groups = this.#groups;
    if (groups.length > 1) {
      body.append(
        this.#select(
          copy.fit,
          groups.map((g) => ({ value: g, label: g })),
          p.group,
          (group) => {
            const target = this.#inGroup(group);
            if (!target) return;
            // the size carries over by value when the other Fit has it available, else that Fit's default size
            const sel = target.options.map(() => /** @type {string | null} */ (null));
            const size = slot.sel[this.#sizeIndex(p)];
            const targetSize = this.#sizeIndex(target);
            if (size != null && target.variants.some((x) => x.available && x.o[targetSize] === size)) sel[targetSize] = size;
            this.#setSlot(i, target, this.#complete(target, sel));
          }
        )
      );
    }

    for (const picker of this.#pickers(p, slot.sel)) {
      const options = picker.values.map((val) => ({
        value: val.value,
        label: val.available ? val.value : fill(copy.sold_out, { value: val.value }),
        disabled: !val.available,
      }));
      if (picker.value == null) {
        options.unshift({ value: '', label: fill(copy.choose_option, { option: picker.name }), disabled: true });
      }
      body.append(
        this.#select(picker.name, options, picker.value ?? '', (value) => {
          if (!value) return;
          const sel = slot.sel.slice();
          sel[picker.index] = value;
          for (let k = picker.index + 1; k < sel.length; k++) {
            if (!p.variants.some((x) => x.available && this.#matches(x, sel.slice(0, k + 1)))) sel[k] = null;
          }
          this.#setSlot(i, p, this.#complete(p, sel));
        })
      );
    }

    el.append(head, media, body);
    return el;
  }

  /** @param {number} i @param {PoolProduct} product @param {(string | null)[]} sel */
  #setSlot(i, product, sel) {
    if (i < 0 || i >= this.#slots.length) return;
    this.#slots[i] = { handle: product.handle, sel, base: null, touched: true };
    this.#renderPanel();
  }

  #lines() {
    return this.#slots.map((slot) => {
      const p = this.#product(slot.handle);
      const variant = this.#resolve(p, slot.sel);
      const open = this.#pickers(p, slot.sel).find((picker) => picker.value == null);
      return { variant: variant && variant.available ? variant : null, missing: open?.name ?? this.#cfg.copy.size };
    });
  }

  #renderActions() {
    const copy = this.#cfg.copy;
    const lines = this.#lines();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button set-builder__button';
    const open = lines.find((line) => !line.variant);
    if (open) {
      button.textContent = fill(copy.cta_incomplete, { option: open.missing });
      button.disabled = true;
    } else {
      button.textContent = copy.cta;
      button.addEventListener('click', () => this.#addSet(button, lines.map((line) => String(line.variant?.id))));
    }
    this.refs.actions.replaceChildren(button);
  }

  /* ── cart ───────────────────────────────────────────────────────────────────────────────── */

  /** @param {HTMLButtonElement} button @param {string[]} variantIds */
  async #addSet(button, variantIds) {
    if (button.getAttribute('aria-busy') === 'true') return; // a double tap adds the set once
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    this.#clearError();

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }
    const lines = variantIds.map((id) => ({ merchandiseId: id, quantity: 1 }));
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise })
    );

    // Horizon declares `Theme` as a top-level const in a classic script: a global binding, not a window property.
    const theme = Theme;
    try {
      const response = await fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      }).then((r) => r.json());

      const cart = await fetch(`${theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then(
        (r) => r.json()
      );

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'set-builder-component', itemCount: lines.length },
        });
        this.#fail(response.description || response.message || this.#cfg.copy.cart_error);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'set-builder-component',
          itemCount: lines.length,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      this.#track('add', { tier: this.#tier, qty: lines.length });

      const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
      if (this.#cfg.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = theme.routes.cart_url;
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#fail(this.#cfg.copy.cart_error);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /** @param {string} message */
  #fail(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = false;
  }

  #clearError() {
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  }

  /* ── the buy box changed ────────────────────────────────────────────────────────────────── */

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || detail.newProduct) return; // a new product re-renders this block (updatedCallback)
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        this.#variantId = String(detail.resource?.id ?? '');
        this.#syncCards(detail.html);
        this.#followMain();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[set-builder] Event promise rejected:', error);
      });
  };

  /** card prices from the re-rendered page @param {Document} html */
  #syncCards(html) {
    const next = html.querySelector(`set-builder-component[data-block-id="${this.dataset.blockId}"]`);
    if (!next) return;
    for (const card of this.refs.cards ?? []) {
      const nextCard = next.querySelector(`[data-tier="${card.dataset.tier}"]`);
      if (!nextCard) continue;
      for (const selector of ['[data-price]', '[data-save]']) {
        const current = card.querySelector(selector);
        const fresh = nextCard.querySelector(selector);
        if (current && fresh) current.replaceWith(document.importNode(fresh, true));
      }
    }
  }

  /** untouched slots follow the buy box */
  #followMain() {
    if (!this.#slots.length) return;
    const key = this.#variantId;
    let changed = false;
    this.#slots.forEach((slot, i) => {
      if (slot.touched || slot.base === key) return;
      this.#slots[i] = this.#freshSlot(i);
      changed = true;
    });
    if (changed && this.#tier != null) this.#renderPanel();
  }
}

/** fills __name__ placeholders @param {string} template @param {Record<string, string | number>} vars */
function fill(template, vars) {
  return String(template || '').replace(/__(\w+)__/g, (match, key) => (vars[key] != null ? String(vars[key]) : match));
}

if (!customElements.get('set-builder-component')) {
  customElements.define('set-builder-component', SetBuilderComponent);
}
